// Shared email-input helpers used by the customer-facing entry + look-up
// screens. Two independent signals -- neither is authoritative on its own:
//
//   isValidEmailFormat(email)
//     Structural check ("local@domain.tld", no spaces, TLD >= 2 chars).
//     Blocks obviously-broken addresses from a form submit.
//
//   suggestEmail(email) -> corrected email OR null
//     Cheaper than a re-type field: matches the domain / TLD against a
//     known-typo map and returns "did you mean X?" when the input is one
//     Levenshtein-ish edit away from a common provider. Not a full
//     mailcheck.js port; extend the maps as production shows new typos.

const DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmaill.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmail.con': 'gmail.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'homail.com': 'hotmail.com',
  'hotmali.com': 'hotmail.com',
  'hotmial.co': 'hotmail.com',
  'yaho.com': 'yahoo.com',
  'yhoo.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yaoo.com': 'yahoo.com',
  'yahoo.con': 'yahoo.com',
  'outlok.com': 'outlook.com',
  'outllook.com': 'outlook.com',
  'outluk.com': 'outlook.com',
  'aoll.com': 'aol.com',
  'aol.co': 'aol.com',
  'iclod.com': 'icloud.com',
  'icoud.com': 'icloud.com',
  'iclould.com': 'icloud.com',
};

// Ordered longest-first so `.comn` matches before `.com`, and `.cmo`
// (a real typo) doesn't get eaten by a shorter alternate. `.co` is a valid
// TLD in the wider web but exceedingly rare for a parent email in a K-12
// school setting -- the false-positive risk of "did you mean .com?" is
// acceptable, since the suggestion is a *suggestion* the user can ignore.
// An array of pairs rather than an object: the longest-first ORDER is the
// contract here, and object key order is only incidentally insertion order.
const TLD_TYPOS = [
  ['.comn', '.com'],
  ['.comm', '.com'],
  ['.cmo', '.com'],
  ['.con', '.com'],
  ['.ocm', '.com'],
  ['.cim', '.com'],
  ['.cm', '.com'],
  ['.co', '.com'],
  ['.om', '.com'],
];

export function isValidEmailFormat(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
}

export function suggestEmail(email) {
  if (!email || !email.includes('@')) return null;
  const [local, domainRaw] = email.split('@');
  if (!domainRaw) return null;
  const domain = domainRaw.toLowerCase();

  if (DOMAIN_TYPOS[domain]) return `${local}@${DOMAIN_TYPOS[domain]}`;

  const hit = TLD_TYPOS.find(([bad]) => domain.endsWith(bad));
  if (!hit) return null;

  const [bad, good] = hit;
  return `${local}@${domain.slice(0, -bad.length)}${good}`;
}
